import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  api,
  applyAdminAuth,
  setAdminSessionId,
  setCsrfToken,
  type AuthMeResponse,
  type PublicConfig,
  type UserProfile,
} from '../services/api';
import {
  initTelegramWebApp,
  isTelegramWebApp,
  resolveTelegramInitData,
  setCachedTelegramInitData,
} from '../utils/telegramWebApp';

interface AuthContextValue {
  user: UserProfile | null;
  loading: boolean;
  config: PublicConfig | null;
  authError: string | null;
  refresh: () => Promise<void>;
  login: (payload: Record<string, unknown>) => Promise<boolean>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function applyAuthResponse(data: AuthMeResponse | undefined): UserProfile | null {
  applyAdminAuth(data);
  if (data?.authenticated && data.user) return data.user;
  return null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [config, setConfig] = useState<PublicConfig | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const res = await api.getMe();
    setUser(applyAuthResponse(res.data));
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const inTelegram = isTelegramWebApp();
        if (inTelegram) {
          initTelegramWebApp();
        }

        const configRes = await api.getConfig();
        if (configRes.data) setConfig(configRes.data);

        let nextUser: UserProfile | null = null;

        if (inTelegram) {
          const initData = await resolveTelegramInitData();
          if (!initData) {
            setAuthError('Telegram не передал данные для входа');
          } else {
            setCachedTelegramInitData(initData);
            const webAppLogin = await api.loginTelegramWebApp(initData);
            nextUser = applyAuthResponse(webAppLogin.data);
            if (!nextUser) {
              setAuthError(webAppLogin.error?.message ?? 'Не удалось войти через Telegram');
            }
          }
        }

        if (!nextUser) {
          const meRes = await api.getMe();
          nextUser = applyAuthResponse(meRes.data);
        }

        setUser(nextUser);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = useCallback(async (payload: Record<string, unknown>) => {
    const res = await api.loginTelegram(payload);
    if (res.success && res.data?.authenticated) {
      setAuthError(null);
      setUser(applyAuthResponse(res.data));
      return true;
    }
    setAuthError(res.error?.message ?? 'Ошибка входа');
    return false;
  }, []);

  const logout = useCallback(async () => {
    await api.logout();
    setUser(null);
    setCsrfToken(null);
    setAdminSessionId(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, config, authError, refresh, login, logout }),
    [user, loading, config, authError, refresh, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
